import React from "react";
import { Layout } from "@/components/Layout";
import { PageHero, SectionHeading, RequestInfoButton } from "@/components/common";
import { useSEO } from "@/lib/useSEO";
import { CONTACT, waLink, WHATSAPP } from "@/config/site";
import { Phone, MessageCircle, Mail, MapPin, Clock } from "lucide-react";

export default function Contact() {
  useSEO({
    title: "Contact Us | Atmasamman Group",
    description: "Connect with Atmasamman Group about technology, education, partnerships, careers or community initiatives.",
    path: "/contact",
  });

  const cards = [
    { icon: Phone, title: "Phone", value: CONTACT.phoneDisplay, href: `tel:${CONTACT.phoneDigits}`, testId: "contact-phone" },
    { icon: MessageCircle, title: "WhatsApp", value: "Chat with us", href: waLink(), testId: "contact-whatsapp", external: true },
    { icon: Mail, title: "Email", value: CONTACT.primaryEmail, href: `mailto:${CONTACT.primaryEmail}`, testId: "contact-email" },
    { icon: MapPin, title: "Office", value: CONTACT.address, testId: "contact-office" },
    { icon: Clock, title: "Business Hours", value: CONTACT.hours, testId: "contact-hours" },
  ];

  return (
    <Layout>
      <PageHero eyebrow="Contact" title="Let's Connect"
        subtitle="Whether you want to explore our organizations, discuss technology, education, partnerships, careers or community initiatives, we'd be happy to hear from you." />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map(({ icon: Icon, title, value, href, testId, external }) => {
              const inner = (
                <>
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#0B132B] text-orange-400"><Icon className="h-6 w-6" /></div>
                  <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{value}</p>
                </>
              );
              const cls = "block rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-orange-500/40 hover:shadow-lg";
              return href ? (
                <a key={title} href={href} data-testid={testId} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className={cls}>{inner}</a>
              ) : (
                <div key={title} data-testid={testId} className={cls}>{inner}</div>
              );
            })}

            <div className="flex flex-col justify-center rounded-2xl border border-orange-200 bg-orange-50/60 p-6">
              <SectionHeading eyebrow="Enquiry" title="Send us your enquiry" />
              <p className="mt-3 text-sm text-slate-600">Use our enquiry form and we'll respond via your preferred channel.</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <RequestInfoButton interest="General Enquiry" testId="contact-request-info" />
                <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-wa-btn"
                  className="inline-flex items-center gap-2 rounded-xl border-2 border-[#25D366] px-6 py-3 text-sm font-semibold text-[#128C4A] transition-all hover:bg-[#25D366] hover:text-white md:text-base">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
